import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useToast } from "@/hooks/use-toast";
import { tasksService, usersService, intakeService } from "@/services/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import { X, Calendar, User, Send, Paperclip, CheckSquare, Plus, AlertCircle } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { cn } from "@/lib/utils";

const STATUSES = ["To Do", "In Progress", "In Review", "Blocked", "Completed", "Pending Approval"];
const PRIORITIES = ["Urgent", "High", "Medium", "Low"];

const STATUS_CONFIG = {
  "To Do": { bg: "bg-gray-100", text: "text-gray-700" },
  "In Progress": { bg: "bg-blue-100", text: "text-blue-700" },
  "In Review": { bg: "bg-purple-100", text: "text-purple-700" },
  "Blocked": { bg: "bg-red-100", text: "text-red-700" },
  "Completed": { bg: "bg-green-100", text: "text-green-700" },
  "Pending Approval": { bg: "bg-amber-100", text: "text-amber-700" }
};

const PRIORITY_CONFIG = {
  "Urgent": { bg: "bg-red-100", text: "text-red-700", icon: "🔴" },
  "High": { bg: "bg-orange-100", text: "text-orange-700", icon: "🟠" },
  "Medium": { bg: "bg-amber-100", text: "text-amber-700", icon: "🟡" },
  "Low": { bg: "bg-green-100", text: "text-green-700", icon: "🟢" }
};

const TaskDetailPanel = ({ task, projects, teamMembers, isOpen, onClose, onUpdate }) => {
  const { toast } = useToast();
  const [taskData, setTaskData] = useState(task);
  const [newComment, setNewComment] = useState("");
  const [newSubtask, setNewSubtask] = useState("");
  const [team, setTeam] = useState([]);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [rejectReason, setRejectReason] = useState("");

  useEffect(() => {
    setTaskData(task);
  }, [task]);

  useEffect(() => {
    const fetchTeam = async () => {
      const users = await usersService.getAll();
      setTeam(users);
    };
    fetchTeam();
  }, []);

  if (!isOpen || !taskData) return null;

  const project = projects?.find(p => p.id === taskData.projectId);
  const assignee = teamMembers?.find(m => m.id === taskData.assignedTo);
  const statusConfig = STATUS_CONFIG[taskData.status] || STATUS_CONFIG["To Do"];
  const priorityConfig = PRIORITY_CONFIG[taskData.priority] || PRIORITY_CONFIG["Medium"];
  
  const subtasks = taskData.subtasks || [];
  const completedSubtasks = subtasks.filter(st => st.completed).length;
  const isOverdue = taskData.dueDate && new Date(taskData.dueDate) < new Date() && taskData.status !== "Completed";

  const getInitials = (name) => {
    if (!name) return "U";
    return name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);
  };

  const getAvatarColor = (name) => {
    const colors = ["from-blue-500 to-blue-600", "from-purple-500 to-purple-600", "from-pink-500 to-pink-600", "from-green-500 to-green-600"];
    const index = name?.charCodeAt(0) || 0;
    return colors[index % colors.length];
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await tasksService.update(taskData.id, { status: newStatus });
      setTaskData(prev => ({ ...prev, status: newStatus }));
      onUpdate && onUpdate();
      toast({ title: "Status updated", description: `Task moved to ${newStatus}`, variant: "success" });
    } catch (error) {
      toast({ title: "Error", description: "Failed to update status", variant: "destructive" });
    }
  };

  const handlePriorityChange = async (newPriority) => {
    try {
      await tasksService.update(taskData.id, { priority: newPriority });
      setTaskData(prev => ({ ...prev, priority: newPriority }));
      onUpdate && onUpdate();
      toast({ title: "Priority updated", variant: "success" });
    } catch (error) {
      toast({ title: "Error", description: "Failed to update priority", variant: "destructive" });
    }
  };

  const handleTitleChange = async (newTitle) => {
    try {
      await tasksService.update(taskData.id, { title: newTitle });
      setTaskData(prev => ({ ...prev, title: newTitle }));
    } catch (error) {
      toast({ title: "Error", description: "Failed to update title", variant: "destructive" });
    }
  };

  const handleDescriptionChange = async (newDescription) => {
    try {
      await tasksService.update(taskData.id, { description: newDescription });
      setTaskData(prev => ({ ...prev, description: newDescription }));
    } catch (error) {
      toast({ title: "Error", description: "Failed to update description", variant: "destructive" });
    }
  };

  const handleDueDateChange = async (newDate) => {
    try {
      await tasksService.update(taskData.id, { dueDate: newDate });
      setTaskData(prev => ({ ...prev, dueDate: newDate }));
    } catch (error) {
      toast({ title: "Error", description: "Failed to update due date", variant: "destructive" });
    }
  };

  const handleAssigneeChange = async (userId) => {
    try {
      await tasksService.update(taskData.id, { assignedTo: userId });
      setTaskData(prev => ({ ...prev, assignedTo: userId }));
      onUpdate && onUpdate();
      toast({ title: "Assignee updated", variant: "success" });
    } catch (error) {
      toast({ title: "Error", description: "Failed to update assignee", variant: "destructive" });
    }
  };

  const handleSubtaskToggle = async (subtaskId, completed) => {
    try {
      const updatedSubtasks = await tasksService.updateSubtask(taskData.id, subtaskId, { completed });
      setTaskData(prev => ({ ...prev, subtasks: updatedSubtasks }));
      onUpdate && onUpdate();
    } catch (error) {
      toast({ title: "Error", description: "Failed to update subtask", variant: "destructive" });
    }
  };

  const handleAddSubtask = async () => {
    if (!newSubtask.trim()) return;
    try {
      const subtask = await tasksService.addSubtask(taskData.id, { title: newSubtask, completed: false });
      setTaskData(prev => ({ ...prev, subtasks: [...(prev.subtasks || []), subtask] }));
      setNewSubtask("");
      onUpdate && onUpdate();
    } catch (error) {
      toast({ title: "Error", description: "Failed to add subtask", variant: "destructive" });
    }
  };

  const handleDeleteSubtask = async (subtaskId) => {
    try {
      const updatedSubtasks = await tasksService.deleteSubtask(taskData.id, subtaskId);
      setTaskData(prev => ({ ...prev, subtasks: updatedSubtasks }));
      onUpdate && onUpdate();
    } catch (error) {
      toast({ title: "Error", description: "Failed to delete subtask", variant: "destructive" });
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    try {
      const comment = await tasksService.addComment(taskData.id, {
        author: teamMembers?.[0]?.name || "User",
        content: newComment
      });
      setTaskData(prev => ({ ...prev, comments: [...(prev.comments || []), comment] }));
      setNewComment("");
      onUpdate && onUpdate();
    } catch (error) {
      toast({ title: "Error", description: "Failed to add comment", variant: "destructive" });
    }
  };

  const isIntakeTask = (taskData?.isIntakeTask === true || taskData?.intakeId) && taskData?.status === "Pending Approval";
  const isPendingApproval = taskData?.status === "Pending Approval";
  const isReadOnly = taskData?.status === "In Progress" || taskData?.status === "To Do" || taskData?.status === "Completed";

  const handleAccept = async () => {
    if (!taskData || !taskData.id) {
      alert("Invalid task data");
      return;
    }
    try {
      const taskId = taskData.id;
      console.log("Accepting task:", taskId);
      await tasksService.update(taskId, { 
        status: "In Progress",
        isIntakeTask: false,
        acceptedAt: new Date()
      });
      
      if (taskData.intakeId) {
        console.log("Updating intake:", taskData.intakeId);
        await intakeService.update(taskData.intakeId, { 
          status: "approved",
          approvedAt: new Date()
        });
      }
      
      setTaskData(prev => ({ ...prev, status: "In Progress", isIntakeTask: false }));
      if (onUpdate) onUpdate();
      alert("Task Accepted! It has been moved to In Progress.");
      if (onClose) onClose();
      if (onClose) onClose();
    } catch (error) {
      console.error("Accept error:", error);
      alert("Failed to accept task. Please try again.");
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      toast({ title: "Reason Required", description: "Please provide a reason for rejecting this task", variant: "destructive" });
      return;
    }
    try {
      if (taskData.intakeId) {
        await intakeService.update(taskData.intakeId, { 
          status: "rejected",
          rejectedAt: new Date(),
          rejectedBy: taskData.assignedTo,
          rejectReason: rejectReason
        });
      }
      
      await tasksService.delete(taskData.id);
      setShowRejectDialog(false);
      setRejectReason("");
      onUpdate && onUpdate();
      toast({ title: "Task Rejected", description: "The intake has been sent back", variant: "success" });
      onClose();
    } catch (error) {
      toast({ name: "Error", description: "Failed to reject task", variant: "destructive" });
    }
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-50" onClick={onClose} />
      <div className="fixed top-0 right-0 h-screen w-full sm:max-w-lg bg-white z-50 shadow-2xl overflow-y-auto animate-slide-in">
        <div className="sticky top-0 bg-white border-b border-slate-200 px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between z-10">
          <h2 className="text-lg sm:text-xl font-bold text-navy">Task Details</h2>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="w-5 h-5" />
          </Button>
        </div>

        <div className="p-4 sm:space-y-6 space-y-4">
          <div className="space-y-2">
            <input
              type="text"
              value={taskData.title}
              onChange={(e) => setTaskData(prev => ({ ...prev, title: e.target.value }))}
              onBlur={(e) => handleTitleChange(e.target.value)}
              className="w-full text-lg sm:text-2xl font-bold text-navy border-none focus:outline-none focus:ring-0 p-0 break-words whitespace-normal"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Status</Label>
              {isReadOnly ? (
                <div className="flex items-center gap-2">
                  <span className={cn("px-3 py-1.5 rounded-full text-sm font-medium", STATUS_CONFIG[taskData.status]?.bg, STATUS_CONFIG[taskData.status]?.text)}>
                    {taskData.status}
                  </span>
                </div>
              ) : isIntakeTask && isPendingApproval ? (
                <div className="flex items-center gap-2">
                  <span className="px-3 py-1.5 rounded-full text-sm font-medium bg-amber-100 text-amber-700">
                    Pending Approval
                  </span>
                </div>
              ) : (
                <div className="flex gap-2 flex-wrap">
                  {STATUSES.map(status => (
                    <button
                      key={status}
                      onClick={() => handleStatusChange(status)}
                      className={cn(
                        "px-3 py-1.5 rounded-full text-sm font-medium transition-all",
                        taskData.status === status 
                          ? STATUS_CONFIG[status].bg + " " + STATUS_CONFIG[status].text
                          : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                      )}
                    >
                      {status}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label>Priority</Label>
              {isReadOnly ? (
                <div className="flex items-center gap-2">
                  <span className={cn("px-3 py-1.5 rounded-full text-sm font-medium", PRIORITY_CONFIG[taskData.priority]?.bg, PRIORITY_CONFIG[taskData.priority]?.text)}>
                    {taskData.priority}
                  </span>
                </div>
              ) : (
                <div className="flex gap-2">
                  {PRIORITIES.map(priority => (
                    <button
                      key={priority}
                      onClick={() => handlePriorityChange(priority)}
                      className={cn(
                        "px-3 py-1.5 rounded-full text-sm font-medium transition-all",
                        taskData.priority === priority
                          ? PRIORITY_CONFIG[priority].bg + " " + PRIORITY_CONFIG[priority].text
                          : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                      )}
                    >
                      {PRIORITY_CONFIG[priority].icon} {priority}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {project && (
            <div className="space-y-2">
              <Label>Project</Label>
              <div 
                className="px-3 py-2 rounded-lg text-sm font-medium w-fit"
                style={{ backgroundColor: `${project.color}20`, color: project.color }}
              >
                {project.name}
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Assigned To</Label>
              {isReadOnly ? (
                <div className="px-3 py-2 bg-slate-100 rounded-lg text-sm text-slate-600 font-medium">
                  {teamMembers?.find(m => m.id === taskData.assignedTo)?.name || "Unassigned"}
                </div>
              ) : (
                <select
                  value={taskData.assignedTo || ""}
                  onChange={(e) => handleAssigneeChange(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                >
                  <option value="">Unassigned</option>
                  {teamMembers?.map(member => (
                    <option key={member.id} value={member.id}>{member.name}</option>
                  ))}
                </select>
              )}
            </div>
            <div className="space-y-2">
              <Label>Due Date</Label>
              {isReadOnly ? (
                <div className="px-3 py-2 bg-slate-100 rounded-lg text-sm text-slate-600 font-medium">
                  {(() => {
                    const dueDateVal = taskData.dueDate || taskData.deadline;
                    if (!dueDateVal) return "Not set";
                    const dateObj = dueDateVal.toDate ? dueDateVal.toDate() : new Date(dueDateVal);
                    return isNaN(dateObj.getTime()) ? "Not set" : dateObj.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
                  })()}
                </div>
              ) : (isIntakeTask || taskData.isIntakeTask) && (taskData.status === "Pending Approval") ? (
                <div className="px-3 py-2 bg-slate-100 rounded-lg text-sm text-slate-600 font-medium">
                  {(() => {
                    const dueDateVal = taskData.dueDate || taskData.deadline;
                    if (!dueDateVal) return "Not set";
                    const dateObj = dueDateVal.toDate ? dueDateVal.toDate() : new Date(dueDateVal);
                    return isNaN(dateObj.getTime()) ? "Not set" : dateObj.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
                  })()}
                </div>
              ) : (
                <Input
                  type="date"
                  value={taskData.dueDate || taskData.deadline || ""}
                  onChange={(e) => handleDueDateChange(e.target.value)}
                  className={cn(isOverdue && "border-red-500")}
                />
              )}
            </div>
          </div>

          <div className="space-y-2">
            <Label>Description</Label>
            {isReadOnly ? (
              <div className="p-3 bg-slate-50 rounded-lg text-sm text-slate-700 min-h-[100px] whitespace-pre-wrap">
                {taskData.description || "No description"}
              </div>
            ) : (
              <Textarea
                value={taskData.description || ""}
                onChange={(e) => setTaskData(prev => ({ ...prev, description: e.target.value }))}
                onBlur={(e) => handleDescriptionChange(e.target.value)}
                placeholder="Add a description..."
                rows={4}
              />
            )}
          </div>

          <div className="space-y-3">
            <Label>Subtasks</Label>
            <div className="space-y-2">
              {subtasks.map(subtask => (
                <div key={subtask.id} className="flex items-center gap-2 group">
                  <Checkbox
                    checked={subtask.completed}
                    onChange={(e) => handleSubtaskToggle(subtask.id, e.target.checked)}
                  />
                  <span className={cn("flex-1 text-sm", subtask.completed && "line-through text-gray-400")}>
                    {subtask.title}
                  </span>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 opacity-0 group-hover:opacity-100"
                    onClick={() => handleDeleteSubtask(subtask.id)}
                  >
                    <X className="w-3 h-3" />
                  </Button>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <Input
                placeholder="Add a subtask..."
                value={newSubtask}
                onChange={(e) => setNewSubtask(e.target.value)}
                onKeyPress={(e) => e.key === "Enter" && handleAddSubtask()}
                className="flex-1"
              />
              <Button size="sm" onClick={handleAddSubtask}>
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            {subtasks.length > 0 && (
              <Progress value={(completedSubtasks / subtasks.length) * 100} className="h-2" />
            )}
          </div>

          {isIntakeTask && (
            <div style={{ display: "flex", flexDirection: "column", gap: "12px", padding: "16px", backgroundColor: "#f0fdf4", borderRadius: "8px", border: "1px solid #22c55e" }}>
              <Label style={{ color: "#166534", fontSize: "18px", fontWeight: "600" }}>Intake Action Required</Label>
              <p style={{ color: "#166534", fontSize: "14px" }}>
                Review this intake request below and decide whether to accept or reject it.
              </p>
              <div style={{ display: "flex", gap: "12px", marginTop: "16px" }}>
                <button 
                  type="button"
                  onClick={() => handleAccept()}
                  style={{ flex: 1, backgroundColor: "#16a34a", color: "white", fontWeight: "600", padding: "12px 16px", borderRadius: "6px", border: "none", cursor: "pointer" }}
                >
                  ✓ Accept Task
                </button>
                <button 
                  type="button"
                  onClick={() => setShowRejectDialog(true)}
                  style={{ flex: 1, backgroundColor: "#dc2626", color: "white", fontWeight: "600", padding: "12px 16px", borderRadius: "6px", border: "none", cursor: "pointer" }}
                >
                  ✗ Reject
                </button>
              </div>
            </div>
          )}

          <AlertDialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Reject Task</AlertDialogTitle>
                <AlertDialogDescription>
                  Please provide a reason for rejecting this task. Thisreason will be visible to the COO who can reassign the task if needed.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Enter reason for rejecting this task..."
                className="w-full p-3 border border-slate-200 rounded-lg min-h-[100px]"
              />
              <AlertDialogFooter>
                <AlertDialogCancel onClick={() => setRejectReason("")}>Cancel</AlertDialogCancel>
                <AlertDialogAction 
                  onClick={handleReject}
                  className="bg-red-500 hover:bg-red-600"
                >
                  Reject Task
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          <div className="space-y-3">
            <Label>Comments</Label>
            <div className="space-y-3 max-h-60 overflow-y-auto">
              {taskData.comments?.map(comment => (
                <div key={comment.id} className="flex gap-3 p-3 bg-slate-50 rounded-lg">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white text-xs font-medium">
                    {getInitials(comment.author)}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm text-navy">{comment.author}</span>
                      <span className="text-xs text-gray-400">
                        {new Date(comment.createdAt).toLocaleString()}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600">{comment.content}</p>
                  </div>
                </div>
              ))}
              {(!taskData.comments || taskData.comments.length === 0) && (
                <p className="text-sm text-gray-400 text-center py-4">No comments yet</p>
              )}
            </div>
            <div className="flex gap-2">
              <Input
                placeholder="Write a comment..."
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                onKeyPress={(e) => e.key === "Enter" && handleAddComment()}
              />
              <Button size="icon" onClick={handleAddComment}>
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default TaskDetailPanel;